import { supabase } from '../lib/supabase'

export type RecurringSeriesSource = 'visit_group' | 'job'

export interface RecurringJobSeries {
  id: string
  source: RecurringSeriesSource
  name: string
  recurrence_type: string | null
  recurrence_days: number[] | null
  start_date: string | null
  end_date: string | null
  scheduled_start_time: string | null
  scheduled_end_time: string | null
  is_active: boolean
  next_scheduled_date: string | null
  occurrence_count: number
  customer_name: string | null
  property_name: string | null
  property_address: string | null
  contract_id: string | null
  contract_number: string | null
  contract_title: string | null
  contract_status: string | null
  operational_division_id: string | null
  service_type: string | null
  job_id: string | null
  job_number: string | null
  job_status: string | null
  assigned_crew_name: string | null
  assigned_employee_name: string | null
}

function customerLabel(customer: any): string | null {
  if (!customer) return null
  return (
    customer.company_name ||
    `${customer.contact_first_name || ''} ${customer.contact_last_name || ''}`.trim() ||
    null
  )
}

function employeeLabel(emp: any): string | null {
  if (!emp) return null
  const name = `${emp.first_name || ''} ${emp.last_name || ''}`.trim()
  return name || null
}

function unwrapOne<T>(value: T | T[] | null | undefined): T | null {
  if (value == null) return null
  return Array.isArray(value) ? value[0] || null : value
}

/** PostgREST returns at most 1000 rows unless the client pages with range(). */
const PAGE_SIZE = 1000
const IN_CHUNK = 100

type JobStatRow = {
  status?: string | null
  scheduled_date?: string | null
}

export function accumulateJobSeriesStats<T extends JobStatRow>(
  rows: T[],
  getSeriesId: (row: T) => string | null | undefined,
  today: string
): { nextById: Map<string, string>; countById: Map<string, number> } {
  const nextById = new Map<string, string>()
  const countById = new Map<string, number>()
  for (const row of rows) {
    const key = getSeriesId(row)
    if (!key) continue
    countById.set(key, (countById.get(key) || 0) + 1)
    if (
      row.status !== 'cancelled' &&
      row.scheduled_date &&
      row.scheduled_date >= today &&
      (!nextById.has(key) || row.scheduled_date < (nextById.get(key) as string))
    ) {
      nextById.set(key, row.scheduled_date)
    }
  }
  return { nextById, countById }
}

/** Keep only Job Create parents that already have child occurrences. */
export function selectTrueJobSeriesParents<T extends { id: string }>(
  candidates: T[],
  childParentIds: Array<string | null | undefined>
): T[] {
  const withChildren = new Set(
    childParentIds.filter((id): id is string => typeof id === 'string' && id.length > 0)
  )
  return candidates.filter((job) => withChildren.has(job.id))
}

async function fetchAllPaged<T>(build: () => any): Promise<T[]> {
  const rows: T[] = []
  let from = 0
  for (;;) {
    const { data, error } = await build().range(from, from + PAGE_SIZE - 1)
    if (error) throw error
    const page = (data || []) as T[]
    rows.push(...page)
    if (page.length < PAGE_SIZE) break
    from += PAGE_SIZE
  }
  return rows
}

function chunkIds(ids: string[]): string[][] {
  const chunks: string[][] = []
  for (let i = 0; i < ids.length; i += IN_CHUNK) {
    chunks.push(ids.slice(i, i + IN_CHUNK))
  }
  return chunks
}

const VISIT_GROUP_SELECT = `
      id,
      name,
      recurrence_type,
      recurrence_days,
      start_date,
      end_date,
      scheduled_start_time,
      scheduled_end_time,
      is_active,
      assigned_crew_id,
      assigned_employee_id,
      crew:crews(name),
      employee:employees(first_name, last_name),
      contract:contracts!inner(
        id,
        contract_number,
        title,
        status,
        service_type,
        operational_division_id,
        customer:customers(company_name, contact_first_name, contact_last_name),
        property:properties(name, address, city, state)
      )
    `

const PARENT_JOB_SELECT = `
      id,
      job_number,
      title,
      status,
      recurrence_type,
      recurrence_days,
      scheduled_date,
      scheduled_start_time,
      scheduled_end_time,
      service_type,
      operational_division_id,
      is_recurring,
      parent_recurring_job_id,
      visit_group_id,
      contract_id,
      assigned_crew_id,
      assigned_employee_id,
      customer:customers(company_name, contact_first_name, contact_last_name),
      property:properties(name, address, city, state),
      contract:contracts!jobs_contract_id_fkey(id, contract_number, title, status),
      crew:crews(name),
      employee:employees(first_name, last_name)
    `

/** Load recurrence series from visit groups + parent recurring jobs (RLS-scoped). */
export async function fetchRecurringJobSeries(): Promise<RecurringJobSeries[]> {
  const today = new Date().toISOString().split('T')[0]

  const groups = await fetchAllPaged<any>(() =>
    supabase.from('contract_visit_groups').select(VISIT_GROUP_SELECT).order('name', {
      ascending: true,
    })
  )

  // Candidate "job series" parents: recurring, no parent, not tied to a visit group.
  // Visit-group generation also sets is_recurring on each visit. When a visit group is
  // replaced, replace_contract_visit_groups nulls visit_group_id on those visits — they
  // must NOT each become a Recurring Jobs row. Only keep true Job Create parents that
  // already have child jobs linked via parent_recurring_job_id.
  const candidateParents = await fetchAllPaged<any>(() =>
    supabase
      .from('jobs')
      .select(PARENT_JOB_SELECT)
      .eq('is_recurring', true)
      .is('parent_recurring_job_id', null)
      .is('visit_group_id', null)
      .order('title', { ascending: true })
  )

  const groupIdSet = new Set(groups.map((g) => g.id as string))
  const candidateParentIds = candidateParents.map((j) => j.id as string)

  let nextByGroup = new Map<string, string>()
  let countByGroup = new Map<string, number>()
  let nextByParent = new Map<string, string>()
  let countByParent = new Map<string, number>()

  if (groupIdSet.size) {
    // Page all visit-group jobs instead of .in(ids) — a single PostgREST page is
    // capped at 1000 rows, and a large UUID IN list can also truncate the URL.
    const groupJobs = await fetchAllPaged<{
      visit_group_id: string | null
      scheduled_date: string | null
      status: string | null
    }>(() =>
      supabase
        .from('jobs')
        .select('visit_group_id, scheduled_date, status')
        .not('visit_group_id', 'is', null)
        .order('id', { ascending: true })
    )
    const stats = accumulateJobSeriesStats(
      groupJobs,
      (row) => (row.visit_group_id && groupIdSet.has(row.visit_group_id) ? row.visit_group_id : null),
      today
    )
    nextByGroup = stats.nextById
    countByGroup = stats.countById
  }

  const childJobs: Array<{
    id: string
    parent_recurring_job_id: string | null
    scheduled_date: string | null
    status: string | null
  }> = []
  if (candidateParentIds.length) {
    for (const ids of chunkIds(candidateParentIds)) {
      const page = await fetchAllPaged<(typeof childJobs)[number]>(() =>
        supabase
          .from('jobs')
          .select('id, parent_recurring_job_id, scheduled_date, status')
          .in('parent_recurring_job_id', ids)
          .order('id', { ascending: true })
      )
      childJobs.push(...page)
    }
  }

  const parentJobs = selectTrueJobSeriesParents(
    candidateParents as Array<{ id: string }>,
    childJobs.map((row) => row.parent_recurring_job_id)
  ) as typeof candidateParents
  const parentIdSet = new Set(parentJobs.map((j) => j.id as string))

  if (parentJobs.length) {
    const combined = [
      ...parentJobs.map((job) => ({
        id: job.id as string,
        parent_recurring_job_id: null as string | null,
        scheduled_date: job.scheduled_date as string | null,
        status: job.status as string | null,
      })),
      ...childJobs.filter(
        (row) => row.parent_recurring_job_id && parentIdSet.has(row.parent_recurring_job_id)
      ),
    ]
    const stats = accumulateJobSeriesStats(
      combined,
      (row) =>
        row.parent_recurring_job_id && parentIdSet.has(row.parent_recurring_job_id)
          ? row.parent_recurring_job_id
          : parentIdSet.has(row.id)
            ? row.id
            : null,
      today
    )
    nextByParent = stats.nextById
    countByParent = stats.countById
  }

  const series: RecurringJobSeries[] = []

  for (const g of groups) {
    const contract = unwrapOne(g.contract as any)
    const customer = unwrapOne(contract?.customer)
    const property = unwrapOne(contract?.property)
    const crew = unwrapOne(g.crew as any)
    const employee = unwrapOne(g.employee as any)

    series.push({
      id: g.id,
      source: 'visit_group',
      name: g.name || contract?.title || 'Visit group',
      recurrence_type: g.recurrence_type || null,
      recurrence_days: g.recurrence_days || null,
      start_date: g.start_date || null,
      end_date: g.end_date || null,
      scheduled_start_time: g.scheduled_start_time || null,
      scheduled_end_time: g.scheduled_end_time || null,
      is_active: g.is_active !== false,
      next_scheduled_date: nextByGroup.get(g.id) || null,
      occurrence_count: countByGroup.get(g.id) || 0,
      customer_name: customerLabel(customer),
      property_name: property?.name || null,
      property_address: property
        ? [property.address, property.city, property.state].filter(Boolean).join(', ')
        : null,
      contract_id: contract?.id || null,
      contract_number: contract?.contract_number || null,
      contract_title: contract?.title || null,
      contract_status: contract?.status || null,
      operational_division_id: contract?.operational_division_id || null,
      service_type: contract?.service_type || null,
      job_id: null,
      job_number: null,
      job_status: null,
      assigned_crew_name: crew?.name || null,
      assigned_employee_name: employeeLabel(employee),
    })
  }

  for (const job of parentJobs) {
    const customer = unwrapOne(job.customer as any)
    const property = unwrapOne(job.property as any)
    const contract = unwrapOne(job.contract as any)
    const crew = unwrapOne(job.crew as any)
    const employee = unwrapOne(job.employee as any)

    series.push({
      id: job.id,
      source: 'job',
      name: job.title || job.job_number || 'Recurring job',
      recurrence_type: job.recurrence_type || null,
      recurrence_days: job.recurrence_days || null,
      start_date: job.scheduled_date || null,
      end_date: null,
      scheduled_start_time: job.scheduled_start_time || null,
      scheduled_end_time: job.scheduled_end_time || null,
      is_active: job.status !== 'cancelled',
      next_scheduled_date: nextByParent.get(job.id) || null,
      occurrence_count: countByParent.get(job.id) || 1,
      customer_name: customerLabel(customer),
      property_name: property?.name || null,
      property_address: property
        ? [property.address, property.city, property.state].filter(Boolean).join(', ')
        : null,
      contract_id: contract?.id || job.contract_id || null,
      contract_number: contract?.contract_number || null,
      contract_title: contract?.title || null,
      contract_status: contract?.status || null,
      operational_division_id: job.operational_division_id || null,
      service_type: job.service_type || null,
      job_id: job.id,
      job_number: job.job_number || null,
      job_status: job.status || null,
      assigned_crew_name: crew?.name || null,
      assigned_employee_name: employeeLabel(employee),
    })
  }

  return series
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

export function formatRecurrenceDays(days: number[] | null | undefined): string {
  if (!days?.length) return '—'
  return days
    .slice()
    .sort((a, b) => a - b)
    .map((d) => DAY_NAMES[d] ?? String(d))
    .join(', ')
}

export function formatRecurrenceType(type: string | null | undefined): string {
  if (!type) return '—'
  return type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}
